import axios from 'axios';

import { Article } from '@/modules/articles/model/article';
import { Page } from '@/shared/pagination/export';
import { Opinion } from '@/modules/articles/model/opinions/opinions';
import { OpinionMap } from '@/modules/articles/model/opinions/opinion-map';

const base = '/api/v1/news';

export type OpinionType = 'BOOKMARK' | 'LIKE';
async function saveOpinion(uri: string, opinion: OpinionType): Promise<Opinion | null> {
  return await axios.put(`${base}/opinions/${uri}/${opinion}`);
}

async function removeOpinion(uri: string, opinion: OpinionType): Promise<null> {
  return await axios.delete(`${base}/opinions/${uri}/${opinion}`);
}

async function getArticlesByOpinion(
  opinion: OpinionType,
  { page, size = 12 }: { page: number; size?: number },
): Promise<Page<Article>> {
  return (await axios.get(`${base}/articles/by-opinion/${opinion}?page=${page}&size=${size}`)).data;
}

async function getOpinionsOfArticles(...uris: string[]): Promise<OpinionMap> {
  return (await axios.get(`${base}/my-opinions?${uris.map(s => `uri=${s}`).join('&')}`)).data;
}

// Calls made in the same tick are collected here and sent as one request.
let batch: { uris: Set<string>; result: Promise<OpinionMap> } | null = null;

/**
 * Opinions of a single article, with every call made in the same tick coalesced into one
 * request. Lists that mount one BookmarkButton per row (the search panel, for instance)
 * otherwise fire one request per visible row at the same instant.
 */
function getOpinionsOfArticleBatched(uri: string): Promise<OpinionMap> {
  if (!batch) {
    const uris = new Set<string>();
    const result = new Promise<OpinionMap>((resolve, reject) => {
      queueMicrotask(() => {
        batch = null;
        getOpinionsOfArticles(...uris).then(resolve, reject);
      });
    });
    batch = { uris, result };
  }
  batch.uris.add(uri);
  return batch.result.then(map => (map[uri] ? { [uri]: map[uri] } : {}));
}

const opinionsApi = {
  saveOpinion,
  removeOpinion,
  getArticlesByOpinion,
  getOpinionsOfArticles,
  getOpinionsOfArticleBatched,
};

export default opinionsApi;
